import { useCallback, useState } from "react";
import { CampaignImportServiceError, importCampaigns } from "../api";
import { CampaignImportResponse, CampaignImportValidationError } from "../types";

interface UseImportCampaignsResult {
  error: string | null;
  importErrors: CampaignImportValidationError[];
  isImporting: boolean;
  execute: (file: File, organizationId?: string) => Promise<CampaignImportResponse>;
  reset: () => void;
}

export function useImportCampaigns(): UseImportCampaignsResult {
  const [error, setError] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<CampaignImportValidationError[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const reset = useCallback(() => {
    setError(null);
    setImportErrors([]);
  }, []);

  const execute = useCallback(
    async (file: File, organizationId?: string) => {
      if (isImporting) {
        throw new Error("Campaign import is already in progress.");
      }

      setIsImporting(true);
      setError(null);
      setImportErrors([]);

      try {
        return await importCampaigns(file, organizationId);
      } catch (importError) {
        if (importError instanceof CampaignImportServiceError) {
          setError(importError.message);
          setImportErrors(importError.importErrors);
        } else {
          setError("Failed to import campaigns.");
        }

        throw importError;
      } finally {
        setIsImporting(false);
      }
    },
    [isImporting],
  );

  return { error, importErrors, isImporting, execute, reset };
}
